/**
 * Integrytas Group — Theme persistence (gateway + localStorage).
 *
 * - ORG / USER themes live in csm_theme (read/written via the API gateway, RLS
 *   scoping reads to ORG + own and writes to own / admin).
 * - The user's selected theme code + density/font-size live in the existing
 *   csm_ui_preference GLOBAL/presentation record (one round-trip, cross-device).
 * - The app-default theme code is the admin-set INTEGRYTAS.THEME.DEFAULT param.
 * - A localStorage cache of the last applied theme lets the login screen paint
 *   themed instantly, before any auth/DB round-trip.
 */

import { apiDb } from '../api_client';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { DEFAULT_THEME, DEFAULT_PALETTE } from './theme_presets';
import { applyTheme, applyDensityFontSize } from './theme_tokens';
import type { ThemeDefinition, Density, FontSize, ThemeMode } from './theme_tokens';

const PREF_PAGE = 'GLOBAL';
const PREF_KEY = 'presentation';
const APP_DEFAULT_PARAM = 'INTEGRYTAS.THEME.DEFAULT';
const CACHE_KEY = 'csm.theme.cache';

export interface PresentationPref {
  theme_code?: string;
  density?: Density;
  fontSize?: FontSize;
}

interface ThemeRow {
  theme_id: number;
  internal_code: string;
  name: string;
  scope: 'ORG' | 'USER';
  mode: ThemeMode;
  owner_user_id: number | null;
  definition: { colors?: Record<string, string>; font?: string; density?: Density; fontSize?: FontSize } | null;
}

function rowToTheme(r: ThemeRow): ThemeDefinition {
  const d = r.definition ?? {};
  return {
    code: r.internal_code,
    name: r.name,
    scope: r.scope === 'ORG' ? 'org' : 'user',
    mode: r.mode,
    colors: (d.colors ?? {}) as ThemeDefinition['colors'],
    font: d.font ?? DEFAULT_THEME.font,
    density: d.density ?? 'compact',
    fontSize: d.fontSize ?? 'medium',
    owner_user_id: r.owner_user_id,
    theme_id: r.theme_id,
  };
}

export async function loadDbThemes(): Promise<ThemeDefinition[]> {
  try {
    const { data, error } = await apiDb
      .from('csm_theme')
      .select('theme_id, internal_code, name, scope, mode, owner_user_id, definition, is_active')
      .eq('is_active', 'Y');
    if (error) return [];
    return ((data ?? []) as ThemeRow[]).map(rowToTheme);
  } catch {
    return [];
  }
}

export async function loadAppDefaultCode(): Promise<string | null> {
  try {
    const { data } = await apiDb
      .from('csm_system_parameters')
      .select('parameter_value')
      .eq('internal_code', APP_DEFAULT_PARAM)
      .maybeSingle();
    return ((data as { parameter_value?: string } | null)?.parameter_value) ?? null;
  } catch {
    return null;
  }
}

export async function saveTheme(def: ThemeDefinition, session: AuthSession, scope: 'USER' | 'ORG'): Promise<ThemeDefinition> {
  const row = {
    internal_code: def.code,
    name: def.name,
    scope,
    mode: def.mode,
    owner_user_id: scope === 'USER' ? session.effective_user.user_id : null,
    definition: { colors: def.colors, font: def.font, density: def.density, fontSize: def.fontSize },
    is_active: 'Y',
    modified_by: getForensicActorId(session),
  };
  const { error } = await apiDb.from('csm_theme').upsert(row, { onConflict: 'internal_code' });
  if (error) throw new Error(error.message);
  return { ...def, scope: scope === 'ORG' ? 'org' : 'user' };
}

export async function deleteThemeRow(themeId: number): Promise<void> {
  const { error } = await apiDb.from('csm_theme').delete().eq('theme_id', themeId);
  if (error) throw new Error(error.message);
}

export async function setAppDefaultCode(code: string, session: AuthSession): Promise<void> {
  const { error } = await apiDb
    .from('csm_system_parameters')
    .update({ parameter_value: code, modified_by: getForensicActorId(session) })
    .eq('internal_code', APP_DEFAULT_PARAM);
  if (error) throw new Error(error.message);
}

export async function loadPresentationPref(userId: number): Promise<PresentationPref> {
  try {
    const { data } = await apiDb
      .from('csm_ui_preference')
      .select('preference_json')
      .eq('user_id', userId)
      .eq('page_code', PREF_PAGE)
      .eq('component_key', PREF_KEY)
      .maybeSingle();
    return ((data as { preference_json?: PresentationPref } | null)?.preference_json) ?? {};
  } catch {
    return {};
  }
}

export async function savePresentationPref(patch: PresentationPref, current: PresentationPref, session: AuthSession): Promise<void> {
  const merged = { ...current, ...patch };
  await apiDb.from('csm_ui_preference').upsert(
    {
      user_id: session.effective_user.user_id,
      page_code: PREF_PAGE,
      component_key: PREF_KEY,
      preference_json: merged,
      modified_by: getForensicActorId(session),
    },
    { onConflict: 'user_id,page_code,component_key' },
  );
}

export function cacheTheme(def: ThemeDefinition): void {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(def)); } catch { /* ignore */ }
}

export function readCachedTheme(): ThemeDefinition | null {
  try {
    const s = localStorage.getItem(CACHE_KEY);
    return s ? (JSON.parse(s) as ThemeDefinition) : null;
  } catch {
    return null;
  }
}

/** Apply the last-used (or default) theme synchronously at cold start, before
 *  React mounts — so the login screen and first paint are already themed. */
export function bootstrapThemeEarly(): void {
  const cached = readCachedTheme() ?? DEFAULT_THEME;
  applyTheme({ ...DEFAULT_THEME, ...cached } as ThemeDefinition, DEFAULT_PALETTE);
  applyDensityFontSize(cached.density ?? 'compact', cached.fontSize ?? 'medium');
}
